import { useEffect, useMemo, useState } from 'react';
import {
  selectUser,
  selectUserGuid,
  useAppSelector,
} from 'slices/commonSelectors';
import {
  getSitrepCollabTransport,
  releaseSitrepCollabTransport,
} from './sitrepCollabTransport';

export type SitrepPresenceViewer = {
  userGuid: string;
  displayName: string;
  color: string;
  updatedAt: number;
};

const HEARTBEAT_MS = 5000;
const STALE_MS = 15000;

const PRESENCE_COLORS = [
  '#0ea5e9',
  '#22c55e',
  '#f59e0b',
  '#a855f7',
  '#ef4444',
  '#14b8a6',
];

function colorForUser(userGuid: string): string {
  let hash = 0;
  for (let i = 0; i < userGuid.length; i += 1) {
    hash = (hash * 31 + userGuid.charCodeAt(i)) >>> 0;
  }
  return PRESENCE_COLORS[hash % PRESENCE_COLORS.length];
}

export function useSitrepDraftPresence(sitrepId: string | null | undefined) {
  const userGuid = useAppSelector(selectUserGuid);
  const user = useAppSelector(selectUser);
  const displayName =
    user?.profile_settings?.userName ||
    user?.user_name ||
    user?.email_address ||
    'User';

  const [viewers, setViewers] = useState<SitrepPresenceViewer[]>([]);

  useEffect(() => {
    if (!sitrepId || !userGuid) return;

    const transport = getSitrepCollabTransport(sitrepId);
    const topic = `sitrep-presence:${sitrepId}`;
    const selfColor = colorForUser(userGuid);

    const publishPresence = (
      type: 'presence-join' | 'presence-heartbeat' | 'presence-leave'
    ) => {
      void transport.publish(topic, type, {
        userGuid,
        displayName,
        color: selfColor,
        updatedAt: Date.now(),
      });
    };

    const unsub = transport.onMessage((envelope) => {
      if (envelope.topic !== topic) return;
      if (
        envelope.type !== 'presence-join' &&
        envelope.type !== 'presence-heartbeat' &&
        envelope.type !== 'presence-leave'
      ) {
        return;
      }

      const remoteGuid = String(envelope.payload.userGuid || '');
      if (!remoteGuid || remoteGuid === userGuid) return;

      if (envelope.type === 'presence-leave') {
        setViewers((prev) => prev.filter((v) => v.userGuid !== remoteGuid));
        return;
      }

      const next: SitrepPresenceViewer = {
        userGuid: remoteGuid,
        displayName: String(envelope.payload.displayName || 'User'),
        color: String(envelope.payload.color || colorForUser(remoteGuid)),
        updatedAt: Number(envelope.payload.updatedAt || Date.now()),
      };

      setViewers((prev) => {
        const without = prev.filter((v) => v.userGuid !== remoteGuid);
        return [...without, next];
      });
    });

    void transport.whenReady().then(() => {
      publishPresence('presence-join');
    });

    const heartbeat = setInterval(() => {
      publishPresence('presence-heartbeat');
    }, HEARTBEAT_MS);

    const prune = setInterval(() => {
      const cutoff = Date.now() - STALE_MS;
      setViewers((prev) => prev.filter((v) => v.updatedAt >= cutoff));
    }, 3000);

    return () => {
      publishPresence('presence-leave');
      unsub();
      clearInterval(heartbeat);
      clearInterval(prune);
      releaseSitrepCollabTransport(sitrepId);
      setViewers([]);
    };
  }, [sitrepId, userGuid, displayName]);

  const others = useMemo(
    () => viewers.filter((v) => v.userGuid !== userGuid),
    [viewers, userGuid]
  );

  return { viewers: others };
}
